import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import SEO from '../components/SEO';
import FaqSection from '../components/FaqSection';
import { getDestinationFaqs, buildFaqLd } from '../lib/faqs';
import { ArrowLeft, Wifi, Clock, Smartphone, Check, Loader2, Globe, Signal, Zap, Infinity, Radio, MapPin, ChevronDown, ChevronUp, X, Phone, MessageSquare } from 'lucide-react';
import DestinationGroupModal from '../components/DestinationGroupModal';
import axios from 'axios';
import { isNative } from '../lib/platform';
import { buyPlanExternally } from '../lib/externalCheckout';
import { hapticMedium } from '../lib/haptics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SimDetails = () => {
  const { countryId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoBuyRef = useRef(false);
  const { allCountries, fetchPlansForCountry, formatPrice, user, loadingCountries, destinationMetaCache, destinationGroups } = useApp();
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === 'dark';
  
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [dataType, setDataType] = useState('fixed'); // 'fixed' | 'unlimited' | 'voice'
  const [selectedDurationIndex, setSelectedDurationIndex] = useState(0); // Index in availableDurations (0 = shortest)
  const [showAllCountries, setShowAllCountries] = useState(false); // Dropdown for countries covered
  const [showPlanDetails, setShowPlanDetails] = useState(null); // Plan ID for popup

  // Group picker — auto-opens if this destination is a member of a group and
  // the user hasn't explicitly chosen a variant yet (?variant=1 after a pick).
  const activeGroup = useMemo(() => {
    if (!countryId || !destinationGroups?.length) return null;
    return destinationGroups.find((g) => (g.members || []).some((m) => m.country_id === countryId)) || null;
  }, [countryId, destinationGroups]);
  const skipGroupPicker = searchParams.get('variant') === '1';
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  useEffect(() => {
    if (activeGroup && !skipGroupPicker) setShowGroupPicker(true);
    else setShowGroupPicker(false);
  }, [activeGroup?.id, skipGroupPicker]);

  // Find country
  const country = useMemo(() => {
    return allCountries.find(c => c.id === countryId);
  }, [allCountries, countryId]);

  // If the URL uses a different case for a real destination (e.g. /destinations/fr
  // or /destinations/ps), resolve it to the single canonical id so Google doesn't
  // treat the variants as duplicate pages.
  const canonicalMatch = useMemo(() => {
    if (country || loadingCountries || !countryId) return null;
    const lc = String(countryId).toLowerCase();
    return allCountries.find(c => String(c.id).toLowerCase() === lc) || null;
  }, [country, loadingCountries, countryId, allCountries]);

  // Track page view for analytics with session tracking
  useEffect(() => {
    if (country && countryId) {
      // Get or create session ID
      let sessionId = sessionStorage.getItem('connectify_session_id');
      if (!sessionId) {
        sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
        sessionStorage.setItem('connectify_session_id', sessionId);
      }
      
      axios.post(`${API}/track/pageview`, {
        country_id: countryId,
        country_name: country.name,
        is_region: country.isRegion || false,
        region: country.region || '',
        page_type: 'destination',
        session_id: sessionId
      }).catch(err => console.log('Tracking error:', err));
    }
  }, [country, countryId]);

  // Deep-link support: /destinations/{id}?buy={packageId} auto-selects that plan
  // and jumps straight to checkout (used by the native mobile reader-app so the
  // buyer doesn't have to reselect the plan in the browser).
  useEffect(() => {
    const buyId = searchParams.get('buy');
    if (!buyId || autoBuyRef.current) return;
    if (!country || !plans || plans.length === 0) return;
    const target = plans.find(
      (p) => String(p.id) === String(buyId) || String(p.packageId) === String(buyId)
    );
    if (!target) return;
    autoBuyRef.current = true;
    setSelectedPlan(target);
    if (isNative()) return;
    const meta = destinationMetaCache[countryId];
    navigate('/checkout', { state: { country, plan: target, carriers: meta?.carriers || [] } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, country, plans, countryId]);

  // Helpers: classify plans into 3 buckets. Plans with calls/SMS live ONLY in the
  // "voice" bucket so they don't double-up in the Fixed/Unlimited lists.
  const isVoicePlan = (p) => !!(p.withCall || p.withSMS);
  const isFixedPlan = (p) => !p.isUnlimited && !isVoicePlan(p);
  const isUnlimitedPlan = (p) => p.isUnlimited && !isVoicePlan(p);

  // Get available durations based on data type filter
  const availableDurations = useMemo(() => {
    let filteredByType = plans;

    if (dataType === 'fixed') {
      filteredByType = plans.filter(isFixedPlan);
    } else if (dataType === 'unlimited') {
      filteredByType = plans.filter(isUnlimitedPlan);
    } else if (dataType === 'voice') {
      filteredByType = plans.filter(isVoicePlan);
    }

    // Get unique durations and sort them
    const durations = [...new Set(filteredByType.map(p => p.durationDays))].sort((a, b) => a - b);
    return durations.length > 0 ? durations : [7]; // Default to 7 if no plans
  }, [plans, dataType]);

  // Reset duration index when data type changes or when durations change
  useEffect(() => {
    setSelectedDurationIndex(0);
  }, [dataType, availableDurations.length]);

  // Auto-switch data type if no plans available for current selection
  useEffect(() => {
    if (plans.length === 0) return; // Don't switch if no plans loaded yet

    const fixedPlans = plans.filter(isFixedPlan);
    const unlimitedPlans = plans.filter(isUnlimitedPlan);
    const voicePlans = plans.filter(isVoicePlan);

    const currentBucket =
      dataType === 'fixed' ? fixedPlans :
      dataType === 'unlimited' ? unlimitedPlans :
      dataType === 'voice' ? voicePlans : fixedPlans;

    if (currentBucket.length === 0) {
      if (fixedPlans.length > 0) setDataType('fixed');
      else if (unlimitedPlans.length > 0) setDataType('unlimited');
      else if (voicePlans.length > 0) setDataType('voice');
    }
  }, [plans, dataType]);

  // Check which plan types are available
  const hasFixedPlans = useMemo(() => plans.some(isFixedPlan), [plans]);
  const hasUnlimitedPlans = useMemo(() => plans.some(isUnlimitedPlan), [plans]);
  const hasVoicePlans = useMemo(() => plans.some(isVoicePlan), [plans]);

  // Current selected duration
  const selectedDuration = availableDurations[selectedDurationIndex] || availableDurations[0];

  // Load plans
  useEffect(() => {
    const loadPlans = async () => {
      if (!countryId) return;
      setLoadingPlans(true);
      try {
        const fetchedPlans = await fetchPlansForCountry(countryId);
        setPlans(fetchedPlans || []);
        if (fetchedPlans?.length > 0) {
          setSelectedPlan(fetchedPlans[0]);
        }
      } catch (error) {
        console.error('Error fetching plans:', error);
        setPlans([]);
      } finally {
        setLoadingPlans(false);
      }
    };
    loadPlans();
  }, [countryId, fetchPlansForCountry]);

  // Filter and sort plans
  const filteredPlans = useMemo(() => {
    let result = [...plans];

    // Filter by data type
    if (dataType === 'fixed') {
      result = result.filter(isFixedPlan);
    } else if (dataType === 'unlimited') {
      result = result.filter(isUnlimitedPlan);
    } else if (dataType === 'voice') {
      result = result.filter(isVoicePlan);
    }

    // Filter by EXACT duration
    result = result.filter(p => p.durationDays === selectedDuration);

    // Sort by data amount (ascending)
    result.sort((a, b) => a.dataAmount - b.dataAmount);

    return result;
  }, [plans, dataType, selectedDuration]);

  // Update selected plan when filters change
  useEffect(() => {
    if (filteredPlans.length > 0 && (!selectedPlan || !filteredPlans.find(p => p.id === selectedPlan.id))) {
      setSelectedPlan(filteredPlans[0]);
    }
  }, [filteredPlans, selectedPlan]);

  const handleBuyNow = () => {
    if (!selectedPlan) return;
    // browser at the marketing website's checkout — Apple "reader app"
    // rule keeps Revolut checkout compliant and avoids the 30% IAP fee.
    if (isNative()) {
      hapticMedium();
      buyPlanExternally({
        planId: selectedPlan.id,
        countryId,
        currency: undefined,
      });
      return;
    }

    const meta = destinationMetaCache[countryId];
    navigate('/checkout', {
      state: {
        country,
        plan: selectedPlan,
        carriers: meta?.carriers || []
      }
    });
  };

  // Build SEO metadata: dynamic per destination with real pricing & plan count.
  // MUST be declared BEFORE the early returns below (React hooks order rule).
  const seoMeta = useMemo(() => {
    const cname = country?.name || 'Travel';
    const isRegion = !!country?.isRegion;
    const kind = isRegion ? 'Regional' : 'Country';

    const priced = plans.filter(p => typeof p.price === 'number' && p.price > 0);
    const minPrice = priced.length > 0 ? Math.min(...priced.map(p => p.price)) : null;
    const maxPrice = priced.length > 0 ? Math.max(...priced.map(p => p.price)) : null;
    const planCount = plans.length;

    const priceFragment = minPrice != null ? ` from A$${minPrice.toFixed(2)}` : '';
    const countFragment = planCount > 0 ? ` — ${planCount} plans available` : '';

    const title = `${cname} eSIM${priceFragment}${countFragment ? countFragment : ''}`;

    const description = minPrice != null
      ? `Buy a ${cname} eSIM starting from A$${minPrice.toFixed(2)}. ${planCount} prepaid data plans, instant activation, no SIM swap. Works on iPhone & Android. Ideal for travellers to ${cname}.`
      : `Buy a prepaid ${cname} eSIM for instant mobile data on your trip. No SIM swapping, works on iPhone & Android, coverage across ${cname}. ${kind} eSIM plans from Connectify.`;

    const keywords = `${cname} eSIM, ${cname} travel data, ${cname} SIM card, ${cname} mobile data, ${cname} tourist SIM, prepaid eSIM ${cname}, buy ${cname} eSIM online`;

    // Product JSON-LD (AggregateOffer)
    const productLd = priced.length > 0 ? {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${cname} Travel eSIM`,
      description: `Prepaid eSIM data plans for ${cname}. Instant activation, no physical SIM required.`,
      brand: { '@type': 'Brand', name: 'Connectify' },
      category: 'Travel eSIM',
      image: country?.image || 'https://customer-assets.emergentagent.com/job_esim-connect-6/artifacts/3a8g7p2b_20571.webp',
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'AUD',
        lowPrice: minPrice.toFixed(2),
        highPrice: (maxPrice || minPrice).toFixed(2),
        offerCount: planCount,
        availability: 'https://schema.org/InStock',
        url: `https://connectify-esims.com/destinations/${countryId}`,
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        reviewCount: '1250',
      },
    } : null;

    const breadcrumbs = [
      { name: 'Home', url: '/' },
      { name: 'All Destinations', url: '/destinations' },
      { name: cname, url: `/destinations/${countryId}` },
    ];

    // Destination-specific FAQs (rendered visibly and as JSON-LD).
    const faqs = getDestinationFaqs(cname, minPrice, isRegion);
    const faqLd = buildFaqLd(faqs);

    return { title, description, keywords, productLd, breadcrumbs, faqs, faqLd };
  }, [country, plans, countryId]);

  if (loadingCountries) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-neutral-500" />
      </div>
    );
  }

  if (!country && canonicalMatch) {
    return <Navigate to={`/destinations/${canonicalMatch.id}`} replace />;
  }

  if (!country) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        <SEO noindex canonical={`/destinations/${countryId}`} />
        <Navbar />
        <div className="pt-32 text-center px-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-neutral-100 mb-4">Destination not found</h1>
          <button onClick={() => navigate('/destinations')} className="text-blue-600 dark:text-blue-400 hover:underline">
            Browse all destinations
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      <SEO
        title={seoMeta.title}
        description={seoMeta.description}
        keywords={seoMeta.keywords}
        canonical={`/destinations/${country.id}`}
        image={country.image}
        breadcrumbs={seoMeta.breadcrumbs}
        structuredData={[
          ...(seoMeta.productLd ? [seoMeta.productLd] : []),
          seoMeta.faqLd,
        ]}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={country.image || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1920&q=80'}
            alt={country.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>

        <div className="relative z-10 h-full flex flex-col justify-end pb-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="absolute top-28 left-4 sm:left-6 lg:left-8 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} /> Back
          </button>

          <div className="flex items-center gap-4">
            {/* Earth icon for regional/global plans, flag for local */}
            {country.isRegion ? (
              <div className="w-16 h-12 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg shadow-lg flex items-center justify-center">
                <Globe size={32} className="text-white" />
              </div>
            ) : (
              <img src={country.flag} alt={`${country.name} flag`} className="w-16 h-12 object-cover rounded-lg shadow-lg" />
            )}
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white">{country.name}</h1>
              <p className="text-white/80 mt-1 flex items-center gap-2">
                {country.isRegion && <Globe size={16} />}
                {country.isRegion ? 'Regional Plan' : country.region}
              </p>
            </div>
          </div>
          
          {/* Network Providers / Countries Covered Info */}
          {destinationMetaCache[countryId] && (
            <div className="mt-4">
              {destinationMetaCache[countryId].geography === 'local' && destinationMetaCache[countryId].carriers?.length > 0 && (
                <div className="flex items-center gap-2 text-white/90">
                  <Radio size={16} className="text-emerald-400" />
                  <span className="text-sm">
                    <span className="font-semibold">Network Providers:</span>{' '}
                    {destinationMetaCache[countryId].carriers.join(', ')}
                  </span>
                </div>
              )}
              {(destinationMetaCache[countryId].geography === 'regional' || destinationMetaCache[countryId].geography === 'global') && 
               destinationMetaCache[countryId].countriesCovered?.length > 0 && (
                <div className="text-white/90">
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-emerald-400 flex-shrink-0" />
                    <span className="text-sm">
                      <span className="font-semibold">{destinationMetaCache[countryId].countriesCovered.length} Countries Covered</span>
                    </span>
                    <button 
                      onClick={() => setShowAllCountries(true)}
                      className="text-sm text-emerald-400 hover:text-emerald-300 flex items-center gap-1 ml-2 transition-colors underline underline-offset-2"
                    >
                      See them all
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Countries Covered Modal */}
          {showAllCountries && destinationMetaCache[countryId]?.countriesCovered && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAllCountries(false)}>
              <div 
                className="bg-white dark:bg-neutral-900 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-blue-600 to-emerald-500 p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Globe size={28} />
                      <div>
                        <h2 className="text-xl font-bold">{country.name}</h2>
                        <p className="text-white/80 text-sm">{destinationMetaCache[countryId].countriesCovered.length} Countries Covered</p>
                      </div>
                    </div>
                    <button 
                      onClick={() => setShowAllCountries(false)}
                      className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    >
                      <X size={24} />
                    </button>
                  </div>
                </div>
                
                {/* Countries List */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {destinationMetaCache[countryId].countriesCovered.map((countryName, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-800 transition-colors">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></span>
                        <span className="text-gray-700 dark:text-neutral-300 text-sm">{countryName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Asia Sprint Promo - Only on Asia regional plan page */}
      {countryId === 'ASIA_REG' && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-y border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <p className="text-center text-gray-700 dark:text-neutral-300">
              <span className="text-amber-600 dark:text-amber-400">✨</span>{' '}
              Just going to the hotspots in Asia? Save $$ and try our{' '}
              <button
                onClick={() => navigate('/destinations/ASS')}
                className="font-bold text-amber-600 dark:text-amber-400 hover:text-amber-700 underline underline-offset-2 transition-colors"
              >
                Asia Sprint Plan
              </button>
              !
            </p>
          </div>
        </div>
      )}

      {/* Europe Lite Promo - Only on Europe regional plan page */}
      {countryId === 'EU' && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-y border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <p className="text-center text-black dark:text-black">
              Looking to save $$? Try our{' '}
              <button
                onClick={() => navigate('/destinations/EUL')}
                className="font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 underline underline-offset-2 transition-colors"
              >
                Europe Lite Plan
              </button>
              {' '}instead! <span className="text-blue-600 dark:text-blue-400">✈️</span>
            </p>
          </div>
        </div>
      )}

      {/* Europe Regional Promo - For individual European country pages */}
      {(() => {
        const EUROPEAN_COUNTRIES = [
          'AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE',
          'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'XK', 'LV', 'LI', 'LT',
          'LU', 'MT', 'MD', 'MC', 'ME', 'NL', 'MK', 'NO', 'PL', 'PT', 'RO', 'RU',
          'SM', 'RS', 'SK', 'SI', 'ES', 'SE', 'CH', 'UA', 'GB', 'VA', 'AX', 'FO',
          'GI', 'GG', 'IM', 'JE', 'SJ', 'NOC', 'IC', 'GL'
        ];
        const isEuropeanCountry = EUROPEAN_COUNTRIES.includes(countryId) && !['EU', 'EUL', 'EUS', 'EUUS'].includes(countryId);
        
        if (isEuropeanCountry) {
          return (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-y border-blue-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <p className="text-center text-gray-900 dark:text-gray-900">
                  <span className="text-blue-600 dark:text-blue-400">🌍</span> Travelling around? Try our{' '}
                  <button
                    onClick={() => navigate('/destinations/EU')}
                    className="font-bold text-blue-700 dark:text-blue-300 hover:text-blue-800 underline underline-offset-2 transition-colors"
                  >
                    Europe Regional Plan
                  </button>
                  !
                </p>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Asia Regional Promo - For individual Asian country pages */}
      {(() => {
        const ASIAN_COUNTRIES = [
          'AF', 'AM', 'AZ', 'BH', 'BD', 'BT', 'BN', 'KH', 'CN', 'GE', 'HK', 'IN',
          'ID', 'IR', 'IQ', 'IL', 'JP', 'JO', 'KZ', 'KW', 'KG', 'LA', 'LB', 'MO',
          'MY', 'MV', 'MN', 'MM', 'NP', 'KP', 'OM', 'PK', 'PS', 'PH', 'QA', 'SA',
          'SG', 'KR', 'LK', 'SY', 'TW', 'TJ', 'TH', 'TL', 'TR', 'TM', 'AE', 'UZ',
          'VN', 'YE', 'CNM', 'JPL', 'OMP', 'AEP', 'RUP'
        ];
        const isAsianCountry = ASIAN_COUNTRIES.includes(countryId) && !['ASIA_REG', 'ASY', 'ASS', 'CHE', 'CHEP', 'ASSE', 'ASSE4', 'ASSEP'].includes(countryId);
        
        if (isAsianCountry) {
          return (
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-y border-amber-200">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                <p className="text-center text-gray-900 dark:text-gray-900">
                  <span className="text-amber-600 dark:text-amber-400">🌏</span> Travelling around? Try our{' '}
                  <button
                    onClick={() => navigate('/destinations/ASIA_REG')}
                    className="font-bold text-amber-700 dark:text-amber-300 hover:text-amber-800 underline underline-offset-2 transition-colors"
                  >
                    Asia Regional Plan
                  </button>
                  !
                </p>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Main Content */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Plans List */}
            <div className="lg:col-span-2 space-y-6">
              {/* Filters */}
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-100 dark:border-neutral-800 shadow-sm">
                {/* Data Type Buttons - Show whichever buckets have plans */}
                {([hasFixedPlans, hasUnlimitedPlans, hasVoicePlans].filter(Boolean).length >= 2) && (
                  <div className="flex flex-wrap gap-3 mb-6">
                    {hasFixedPlans && (
                      <button
                        onClick={() => setDataType('fixed')}
                        className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-4 rounded-xl font-bold text-sm transition-all border-2 ${
                          dataType === 'fixed'
                            ? 'bg-stone-400 text-white shadow-lg dark:border-white border-black'
                            : 'bg-black text-white hover:bg-stone-600 dark:border-white border-black'
                        }`}
                        data-testid="filter-fixed-gb"
                      >
                        <Wifi size={18} />
                        Fixed GB
                      </button>
                    )}
                    {hasUnlimitedPlans && (
                      <button
                        onClick={() => setDataType('unlimited')}
                        className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-4 rounded-xl font-bold text-sm transition-all border-2 ${
                          dataType === 'unlimited'
                            ? 'bg-stone-400 text-white shadow-lg dark:border-white border-black'
                            : 'bg-black text-white hover:bg-stone-600 dark:border-white border-black'
                        }`}
                        data-testid="filter-unlimited"
                      >
                        <Infinity size={18} />
                        Unlimited Data
                      </button>
                    )}
                    {hasVoicePlans && (
                      <button
                        onClick={() => setDataType('voice')}
                        className={`flex-1 min-w-[160px] flex items-center justify-center gap-2 px-4 py-4 rounded-xl font-bold text-sm transition-all border-2 ${
                          dataType === 'voice'
                            ? 'bg-stone-400 text-white shadow-lg dark:border-white border-black'
                            : 'bg-black text-white hover:bg-stone-600 dark:border-white border-black'
                        }`}
                        data-testid="filter-data-calls-text"
                      >
                        <Phone size={18} />
                        Data, Calls &amp; Text
                      </button>
                    )}
                  </div>
                )}

                {/* Show label when only one type is available */}
                {([hasFixedPlans, hasUnlimitedPlans, hasVoicePlans].filter(Boolean).length === 1) && plans.length > 0 && (
                  <div className="mb-6 flex items-center gap-2 text-gray-600 dark:text-neutral-400">
                    {hasFixedPlans && (
                      <>
                        <Wifi size={18} />
                        <span className="font-semibold">Fixed GB Plans</span>
                      </>
                    )}
                    {hasUnlimitedPlans && (
                      <>
                        <Infinity size={18} />
                        <span className="font-semibold">Unlimited Data Plans</span>
                      </>
                    )}
                    {hasVoicePlans && (
                      <>
                        <Phone size={18} />
                        <span className="font-semibold">Data, Calls &amp; Text Plans</span>
                      </>
                    )}
                  </div>
                )}

                {/* Duration Slider - Clean minimal design */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-600 dark:text-neutral-400">Trip Duration</label>
                    <span className="text-lg font-black text-gray-900 dark:text-neutral-100">
                      {selectedDuration} {selectedDuration === 1 ? 'Day' : 'Days'}
                    </span>
                  </div>
                  
                  {/* Slider */}
                  <div className="relative pt-1">
                    <input
                      type="range"
                      min={0}
                      max={availableDurations.length - 1}
                      value={selectedDurationIndex}
                      onChange={(e) => setSelectedDurationIndex(parseInt(e.target.value))}
                      className="w-full h-1.5 bg-gray-200 dark:bg-neutral-700 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: isDark
                          ? `linear-gradient(to right, #fff 0%, #fff ${(selectedDurationIndex / Math.max(availableDurations.length - 1, 1)) * 100}%, #404040 ${(selectedDurationIndex / Math.max(availableDurations.length - 1, 1)) * 100}%, #404040 100%)`
                          : `linear-gradient(to right, #000 0%, #000 ${(selectedDurationIndex / Math.max(availableDurations.length - 1, 1)) * 100}%, #e5e7eb ${(selectedDurationIndex / Math.max(availableDurations.length - 1, 1)) * 100}%, #e5e7eb 100%)`
                      }}
                      data-testid="duration-slider"
                    />
                    {/* Custom thumb styles */}
                    <style>{`
                      input[type="range"]::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        appearance: none;
                        width: 20px;
                        height: 20px;
                        background: #000;
                        border-radius: 50%;
                        cursor: pointer;
                        border: 2px solid #000;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                      }
                      input[type="range"]::-moz-range-thumb {
                        width: 20px;
                        height: 20px;
                        background: #000;
                        border-radius: 50%;
                        cursor: pointer;
                        border: 2px solid #000;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                      }
                      .dark input[type="range"]::-webkit-slider-thumb {
                        background: #000;
                        border: 2px solid #ffffff;
                        box-shadow: 0 0 0 1px rgba(255,255,255,0.25), 0 2px 6px rgba(0,0,0,0.6);
                      }
                      .dark input[type="range"]::-moz-range-thumb {
                        background: #000;
                        border: 2px solid #ffffff;
                        box-shadow: 0 0 0 1px rgba(255,255,255,0.25), 0 2px 6px rgba(0,0,0,0.6);
                      }
                    `}</style>
                  </div>
                  
                  {/* Key markers - show only a few key points */}
                  <div className="flex justify-between text-xs text-gray-400 dark:text-neutral-500 px-1">
                    {(() => {
                      const durations = availableDurations;
                      const len = durations.length;
                      if (len <= 5) {
                        return durations.map(d => <span key={d}>{d}</span>);
                      }
                      // Show key markers: first, 25%, 50%, 75%, last
                      const markers = [
                        durations[0],
                        durations[Math.floor(len * 0.25)],
                        durations[Math.floor(len * 0.5)],
                        durations[Math.floor(len * 0.75)],
                        durations[len - 1]
                      ];
                      // Remove duplicates while preserving order
                      const uniqueMarkers = [...new Set(markers)];
                      return uniqueMarkers.map(d => <span key={d}>{d}</span>);
                    })()}
                  </div>
                </div>

                {/* Reset Filter */}
                {dataType !== 'all' && (
                  <button
                    onClick={() => { setDataType('all'); setSelectedDurationIndex(0); }}
                    className="mt-4 text-sm text-gray-500 dark:text-neutral-500 hover:text-black dark:hover:text-white underline"
                  >
                    Reset filters
                  </button>
                )}
              </div>

              {/* Plans */}
              {loadingPlans ? (
                <div className="space-y-4">
                  {Array(4).fill(0).map((_, i) => (
                    <div key={i} className="animate-pulse bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-100 dark:border-neutral-800">
                      <div className="h-6 bg-gray-200 dark:bg-neutral-700 rounded w-1/3 mb-4" />
                      <div className="h-4 bg-gray-200 dark:bg-neutral-700 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : filteredPlans.length === 0 ? (
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-12 text-center border border-gray-100 dark:border-neutral-800">
                  <Wifi className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  {dataType === 'fixed' ? (
                    <>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-2">No fixed GB plans available</h3>
                      <p className="text-gray-600 dark:text-neutral-400 mb-4">No fixed GB plans are available for this destination.</p>
                      {hasUnlimitedPlans && (
                        <button
                          onClick={() => setDataType('unlimited')}
                          className="text-emerald-600 dark:text-emerald-400 font-semibold hover:text-emerald-700 underline"
                        >
                          Check out our unlimited data plans instead!
                        </button>
                      )}
                    </>
                  ) : dataType === 'unlimited' ? (
                    <>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-2">No unlimited data plans available</h3>
                      <p className="text-gray-600 dark:text-neutral-400 mb-4">No unlimited data plans are available for this destination.</p>
                      {hasFixedPlans && (
                        <button
                          onClick={() => setDataType('fixed')}
                          className="text-emerald-600 dark:text-emerald-400 font-semibold hover:text-emerald-700 underline"
                        >
                          Check out our fixed GB plans instead!
                        </button>
                      )}
                    </>
                  ) : dataType === 'voice' ? (
                    <>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-2">No data, calls &amp; text plans available</h3>
                      <p className="text-gray-600 dark:text-neutral-400 mb-4">No plans with calls/SMS are available for this destination.</p>
                      {hasFixedPlans && (
                        <button
                          onClick={() => setDataType('fixed')}
                          className="text-emerald-600 dark:text-emerald-400 font-semibold hover:text-emerald-700 underline"
                        >
                          Browse our fixed GB plans instead!
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-2">No plans available</h3>
                      <p className="text-gray-600 dark:text-neutral-400">Try adjusting your filters or check back later.</p>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredPlans.map((plan) => (
                    <div
                      key={plan.id}
                      className={`bg-white dark:bg-neutral-900 rounded-2xl border-2 transition-all cursor-pointer overflow-hidden ${
                        selectedPlan?.id === plan.id 
                          ? 'border-black dark:border-white shadow-lg' 
                          : 'border-gray-100 dark:border-white/40 hover:border-gray-300 dark:hover:border-white'
                      }`}
                      onClick={() => setSelectedPlan(plan)}
                      data-testid={`plan-card-${plan.id}`}
                    >
                      <div className="p-5">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                              {plan.isUnlimited ? (
                                <span className="text-2xl font-black text-gray-900 dark:text-neutral-100">Unlimited</span>
                              ) : (
                                <span className="text-2xl font-black text-gray-900 dark:text-neutral-100">{plan.dataAmount}GB</span>
                              )}
                              {plan.isUnlimited && (
                                <span className="px-2 py-1 bg-green-100 dark:bg-emerald-500/15 text-green-800 dark:text-emerald-300 rounded-full text-xs font-bold">
                                  UNLIMITED
                                </span>
                              )}
                              {/* DATA, CALLS + TEXT Tag */}
                              {(plan.withCall || plan.withSMS) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowPlanDetails(plan.id);
                                  }}
                                  className="px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full text-xs font-bold hover:from-purple-600 hover:to-pink-600 transition-all flex items-center gap-1"
                                >
                                  <Phone size={12} />
                                  DATA, CALLS + TEXT
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-neutral-400">
                              <span className="flex items-center gap-1">
                                <Clock size={14} /> {plan.durationDays} days
                              </span>
                              <span className="flex items-center gap-1">
                                <Signal size={14} /> {plan.signal || '5G/LTE'}
                              </span>
                            </div>
                            {plan.isUnlimited && plan.unlimitedDetails && (
                              <p className="text-xs text-gray-500 dark:text-neutral-500 mt-2">{plan.unlimitedDetails}</p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-black text-gray-900 dark:text-neutral-100">{formatPrice(plan.price)}</p>
                            <p className="text-xs text-gray-500 dark:text-neutral-500">
                              {formatPrice(plan.price / plan.durationDays)}/day
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Selection Indicator */}
                      {selectedPlan?.id === plan.id && (
                        <div className="bg-black text-white dark:bg-white dark:text-black text-center py-2 text-sm font-bold border-t-2 border-black dark:border-white">
                          <Check className="inline mr-2" size={16} /> Selected
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar - Order Summary */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 bg-white dark:bg-neutral-900 rounded-2xl border border-gray-100 dark:border-neutral-800 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-100 dark:border-neutral-800">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-neutral-100 mb-4">Order Summary</h3>
                  
                  {selectedPlan ? (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-neutral-400">Destination</span>
                        <span className="font-semibold text-gray-900 dark:text-neutral-100">{country.name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-neutral-400">Plan</span>
                        <span className="font-semibold text-gray-900 dark:text-neutral-100">
                          {selectedPlan.isUnlimited ? 'Unlimited' : `${selectedPlan.dataAmount}GB`}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 dark:text-neutral-400">Duration</span>
                        <span className="font-semibold text-gray-900 dark:text-neutral-100">{selectedPlan.durationDays} days</span>
                      </div>
                      <div className="h-px bg-gray-100 dark:bg-neutral-800 my-4" />
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-bold text-gray-900 dark:text-neutral-100">Total</span>
                        <span className="text-2xl font-black text-gray-900 dark:text-neutral-100">{formatPrice(selectedPlan.price)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-neutral-500 text-center py-4">Select a plan to continue</p>
                  )}
                </div>

                <div className="p-6">
                  <button
                    onClick={handleBuyNow}
                    disabled={!selectedPlan}
                    className="w-full py-4 bg-black text-white rounded-xl font-bold text-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:border-2 dark:border-white"
                    data-testid="buy-now-btn"
                  >
                    Buy Now
                  </button>
                  
                  <div className="mt-6 space-y-3">
                    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-neutral-400">
                      <Zap size={18} className="text-green-600 dark:text-emerald-400 flex-shrink-0" />
                      <span>Easy one-tap install on Android and iOS</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-neutral-400">
                      <Smartphone size={18} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                      <span>Works on eSIM compatible devices</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-neutral-400">
                      <Check size={18} className="text-green-600 dark:text-emerald-400 flex-shrink-0" />
                      <span>24/7 customer support</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Plan Details Popup - DATA, CALLS + TEXT */}
      {showPlanDetails && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowPlanDetails(null)}
        >
          <div 
            className="bg-white dark:bg-neutral-900 rounded-3xl max-w-md w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const plan = filteredPlans.find(p => p.id === showPlanDetails);
              if (!plan) return null;
              
              const formatAmount = (amount, unit) => {
                if (amount >= 9007199254740990) return 'Unlimited';
                if (amount === 0) return 'Not included';
                return `${amount} ${unit}`;
              };
              
              return (
                <>
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 text-white">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-xl font-bold">Plan Includes</h2>
                      <button 
                        onClick={() => setShowPlanDetails(null)}
                        className="p-1 hover:bg-white/20 rounded-lg transition-colors"
                      >
                        <X size={24} />
                      </button>
                    </div>
                    <p className="text-white/80 text-sm">
                      {plan.isUnlimited ? 'Unlimited Data' : `${plan.dataAmount}GB`} • {plan.durationDays} Days
                    </p>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    {/* Data */}
                    <div className="flex items-center gap-4 p-4 bg-blue-50 dark:bg-blue-500/10 rounded-2xl">
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                        <Wifi className="text-white" size={24} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 dark:text-neutral-500">Mobile Data</p>
                        <p className="text-xl font-bold text-gray-900 dark:text-neutral-100">
                          {plan.isUnlimited ? 'Unlimited' : `${plan.dataAmount}GB`}
                        </p>
                      </div>
                    </div>
                    
                    {/* Calls */}
                    <div className={`flex items-center gap-4 p-4 rounded-2xl ${plan.withCall ? 'bg-green-50' : 'bg-gray-50'}`}>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${plan.withCall ? 'bg-green-500' : 'bg-gray-300'}`}>
                        <Phone className="text-white" size={24} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 dark:text-neutral-500">Voice Calls</p>
                        <p className={`text-xl font-bold ${plan.withCall ? 'text-gray-900' : 'text-gray-400'}`}>
                          {plan.withCall ? formatAmount(plan.callAmount, plan.callUnit) : 'Not included'}
                        </p>
                      </div>
                    </div>
                    
                    {/* SMS */}
                    <div className={`flex items-center gap-4 p-4 rounded-2xl ${plan.withSMS ? 'bg-purple-50' : 'bg-gray-50'}`}>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${plan.withSMS ? 'bg-purple-500' : 'bg-gray-300'}`}>
                        <MessageSquare className="text-white" size={24} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-500 dark:text-neutral-500">Text Messages (SMS)</p>
                        <p className={`text-xl font-bold ${plan.withSMS ? 'text-gray-900' : 'text-gray-400'}`}>
                          {plan.withSMS ? formatAmount(plan.smsAmount, 'SMS') : 'Not included'}
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => {
                        setSelectedPlan(plan);
                        setShowPlanDetails(null);
                      }}
                      className="w-full py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
                    >
                      Select This Plan
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      <FaqSection
        faqs={seoMeta.faqs}
        title={`Frequently asked questions about ${country.name} eSIMs`}
        testIdPrefix="destination-faq"
      />

      <Footer />

      {/* Group picker — opens automatically when this country is a member of
          an admin-defined destination group. Picking a variant navigates to
          that variant's detail page with `?variant=1` so the picker doesn't
          re-open; "Close" also drops `?variant=1` so the user stays here. */}
      {activeGroup && showGroupPicker ? (
        <DestinationGroupModal
          group={activeGroup}
          allCountries={allCountries}
          onClose={() => {
            setShowGroupPicker(false);
            navigate(`/destinations/${countryId}?variant=1`, { replace: true });
          }}
          onPick={(id) => {
            setShowGroupPicker(false);
            if (id === countryId) {
              navigate(`/destinations/${countryId}?variant=1`, { replace: true });
            } else {
              navigate(`/destinations/${id}?variant=1`);
            }
          }}
        />
      ) : null}
    </div>
  );
};

export default SimDetails;
